import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import AuthContext from '../../middlewares/AuthContext'
import SelectSearch from 'react-select-search'
import countries from '../../countries.json'
import states from '../../states.json'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDollar, faShoppingCart } from '@fortawesome/free-solid-svg-icons'
import CustomLoader from '../layouts/CustomLoader'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface AdvertisingInfo {
  price: number
}

interface CheckoutForm {
  months: number
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const CreateAdvertising: React.FC = () => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()

  const [advertising, setAdvertising]   = useState<AdvertisingInfo | null>(null)
  const [loaded, setLoaded]             = useState(false)
  const [loading, setLoading]           = useState(false)
  const [country, setCountry]           = useState('')
  const [availStates, setAvailStates]   = useState<any[]>([])
  const [state, setState]               = useState<string[]>([])
  const [notAvailable, setNotAvailable] = useState<string[]>([])
  const [countryErr, setCountryErr]     = useState(false)
  const [stateErr, setStateErr]         = useState(false)

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get(`${url}marketings/show`, { headers })
        setAdvertising(res.data.message)
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetchInfo()
  }, [])

  const changeCountry = async (val: any) => {
    setCountry(val)
    setCountryErr(false)
    const fd = (countries as any[]).find(c => c.name === val)
    if (!fd) return
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}marketings/advertising/${val}`, { headers })
      const notAvail: string[] = res.data.message
      setNotAvailable(notAvail)
      setState([])
      setAvailStates(
        (states as any[]).filter(s => s.country_id === fd.id && !notAvail.includes(s.name))
      )
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const checkoutAdvertising = async (values: CheckoutForm) => {
    if (!country.trim()) {
      setCountryErr(true)
      return
    }
    setCountryErr(false)
    if (state.length === 0) {
      setStateErr(true)
      return
    }
    setStateErr(false)

    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}marketings/advertising`, {
        ...values,
        states : state,
        country,
      }, { headers })
      if (res.status === 200 && res.data.status === 'success') {
        window.location.href = res.data.message
      }
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const formSchema = Yup.object().shape({
    months: Yup.number().oneOf([3, 6, 12], t('marketing.advertising.create.only_values')).required(),
  })

  if (!loaded) return <CustomLoader />

  return (
    <div className="row justify-content-center">
      <div className="col-lg-10">

        {/* Header card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
          marginBottom   : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, backgroundColor: `${BRAND}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faCommentDollar} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('marketing.advertising.create.you_can')}
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {t('marketing.advertising.create.select_a_country')}
              </p>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>

            <Formik
              initialValues={{ months: 0 } as CheckoutForm}
              validationSchema={formSchema}
              onSubmit={checkoutAdvertising}
            >
              {({ values }) => (
                <Form>
                  <div className="row g-4">

                    {/* Left: country + state selectors */}
                    <div className="col-md-6">
                      <div className="mb-4">
                        <FieldLabel>{t('marketing.advertising.create.chose_your_country')}</FieldLabel>
                        <SelectSearch
                          options={countries as any}
                          value={country}
                          onChange={changeCountry}
                          // name="country"
                          placeholder={t('marketing.advertising.create.chose_your_country')}
                          search
                        />
                        {countryErr && (
                          <p className="text-danger small fw-bold mt-1 mb-0">
                            {t('marketing.advertising.create.chose_your_country')}
                          </p>
                        )}
                      </div>

                      {availStates.length > 0 && (
                        <div className="mb-4">
                          <FieldLabel>{t('marketing.advertising.create.states_selected')}</FieldLabel>
                          <SelectSearch
                            options={availStates}
                            value={state as any}
                            onChange={(val: any) => { setState(val); setStateErr(false) }}
                            // name="state"
                            placeholder={t('marketing.advertising.create.states_selected')}
                            search
                            multiple
                            closeOnSelect
                          />
                          {stateErr && (
                            <p className="text-danger small fw-bold mt-1 mb-0">
                              {t('marketing.advertising.create.select_a_state')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right: selected/not-available state lists */}
                    <div className="col-md-6">
                      <div style={{ backgroundColor: '#f8fafc', borderRadius: 12, padding: '1rem', marginBottom: '0.75rem' }}>
                        <p style={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                          {t('marketing.advertising.create.states_selected')}
                        </p>
                        {state.length > 0
                          ? <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {state.map((s, i) => (
                                <li key={i} style={{ fontSize: '0.85rem', color: '#1e293b', padding: '0.2rem 0' }}>{s}</li>
                              ))}
                            </ul>
                          : <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>—</p>
                        }
                      </div>

                      {notAvailable.length > 0 && (
                        <div style={{ backgroundColor: '#fff5f5', borderRadius: 12, padding: '1rem' }}>
                          <p style={{ fontWeight: 700, color: '#ef4444', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                            {t('marketing.advertising.create.states_not_available')}
                          </p>
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {notAvailable.map((s, i) => (
                              <li key={i} style={{ fontSize: '0.85rem', color: '#ef4444', padding: '0.2rem 0' }}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Months selector */}
                    <div className="col-md-6">
                      <FieldLabel>{t('utilities.months')}</FieldLabel>
                      <Field as="select" name="months" className="form-select form-select-sm">
                        <option value={0} disabled>{t('marketing.advertising.create.select_option')}</option>
                        {[3, 6, 12].map(m => (
                          <option key={m} value={m}>{m} {t('utilities.months')}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="months" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    {/* Total amount */}
                    <div className="col-md-6">
                      <FieldLabel>{t('marketing.advertising.create.total_amount')}</FieldLabel>
                      <div style={{
                        padding        : '0.45rem 0.75rem',
                        borderRadius   : 8,
                        backgroundColor: '#f0fdf4',
                        border         : '1px solid #bbf7d0',
                        minHeight      : 38,
                        display        : 'flex',
                        alignItems     : 'center',
                      }}>
                        {advertising && state.length > 0 && values.months > 0
                          ? <b style={{ color: BRAND_DARK, fontSize: '1rem' }}>
                              ${((advertising.price * state.length) * values.months).toFixed(2)} USD
                            </b>
                          : <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                        }
                      </div>
                    </div>

                    {/* Submit */}
                    <div className="col-12">
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          display        : 'inline-flex',
                          alignItems     : 'center',
                          gap            : '0.45rem',
                          padding        : '0.55rem 1.75rem',
                          borderRadius   : 9999,
                          backgroundColor: loading ? '#94a3b8' : BRAND,
                          color          : '#fff',
                          fontWeight     : 700,
                          fontSize       : '0.875rem',
                          border         : 'none',
                          cursor         : loading ? 'not-allowed' : 'pointer',
                          boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                          transition     : 'filter 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                        onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                      >
                        {loading
                          ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          : <FontAwesomeIcon icon={faShoppingCart} />
                        }
                        <span>{t('marketing.advertising.create.go_to_checkout')}</span>
                      </button>
                    </div>

                  </div>
                </Form>
              )}
            </Formik>

          </div>
        </div>

      </div>
    </div>
  )
}

export default CreateAdvertising
